// --- Task field conditions (serializable subset of FieldCondition) ---

export type TaskConditionType = 'fieldValue' | 'customerContext' | 'taskOutcome';
export type TaskConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'greaterThan'
  | 'lessThan'
  | 'contains'
  | 'in';

/**
 * Which aspect of a source task a `taskOutcome` condition reads:
 * - `outcome` — the source task's selected dropdown option (a string).
 * - `completed` — whether the source task is marked done (a boolean).
 */
export type TaskOutcomeAspect = 'outcome' | 'completed';

export type TaskConditionScalar = string | number | boolean;

export interface TaskFieldCondition {
  type: TaskConditionType;
  /**
   * Source key for `fieldValue` / `customerContext` conditions. Unused
   * (and typically omitted) for `taskOutcome` conditions, which key off
   * `taskId` instead.
   */
  fieldKey?: string;
  /**
   * Source task id for `taskOutcome` conditions — another task in the
   * SAME task list. The condition reads that task's live state (see
   * `aspect`) and compares it via `operator`/`value`.
   */
  taskId?: string;
  /**
   * For `taskOutcome` conditions, which aspect of the source task to
   * read. Defaults to `outcome`. Ignored for other condition types.
   */
  aspect?: TaskOutcomeAspect;
  operator: TaskConditionOperator;
  /**
   * For most operators this is a single scalar. For `in` it is the
   * array of allowed values — the condition evaluates true when the
   * source value matches any entry. For a `completed` taskOutcome
   * condition it is a boolean (`true` = must be done).
   */
  value: TaskConditionScalar | TaskConditionScalar[];
}

// --- Task statuses ---

/**
 * Binary completion status: `new` = not done, `completed` = done. The
 * legacy `could-not-action` / `n-a` / `not-required` values were removed
 * when the checklist moved to a simple checkbox/dropdown model; any old
 * persisted value hydrates as `new`.
 */
export type TaskStatus = 'new' | 'completed';

// --- Task list definitions (blob: task-lists.json) ---

export interface TaskDefinition {
  /** Globally unique task identifier */
  taskId: string;
  title: string;
  description?: string;
  /**
   * Optional reference URL the technician can open in one click while
   * working the task (e.g. a portal, doc, or form). Rendered as a link
   * button on the task row. Must be an http(s) URL.
   */
  url?: string;
  /** Visual subheading within a section (display only) */
  groupLabel?: string;
  showWhen?: TaskFieldCondition;
  /**
   * When true, the technician must record a note before this task counts
   * as done (and thus before the ticket can be resolved). Default false.
   */
  noteRequired?: boolean;
  /**
   * When set, this task is a dropdown task: instead of a checkbox it shows
   * the reusable option set with this id (see TaskDropdownDefinition).
   * Selecting any option marks the task done.
   */
  dropdownId?: string;
}

export interface TaskSectionDefinition {
  /** Unique within the task list */
  sectionId: string;
  label: string;
  showWhen?: TaskFieldCondition;
  tasks: TaskDefinition[];
}

export interface TaskListDefinition {
  taskListId: string;
  displayName: string;
  description?: string;
  schemaVersion: number;
  /**
   * Ordered list of sections. Each entry is either an inline section
   * definition or a reference to another TaskList — the same shape as
   * `TemplateTaskConfig.sections`. Use `isTaskListReference` to
   * discriminate.
   *
   * Composing TaskLists from other TaskLists enables "atomic"
   * procedures (e.g. `wipe-windows`, `setup-arm-windows`) to be
   * reused inside higher-level "bundle" lists. Cycles are detected
   * and reported as warnings at resolution time.
   */
  sections: Array<TaskSectionDefinition | TaskListReference>;
}

export interface TaskListCatalog {
  schemaVersion: number;
  taskLists: TaskListDefinition[];
}

// --- Reusable task dropdowns (table: cr001_task_dropdownses) ---

/**
 * A reusable named option set for dropdown tasks (e.g. "Speedtest
 * Results" → ["cannot connect to WiFi", "500Mbps+", …]). Referenced from
 * a TaskDefinition by `dropdownId`, so the same options can be attached
 * to many tasks.
 */
export interface TaskDropdownDefinition {
  dropdownId: string;
  displayName: string;
  description?: string;
  options: string[];
}

export interface TaskDropdownCatalog {
  schemaVersion: number;
  dropdowns: TaskDropdownDefinition[];
}

// --- Template task config (blob: template-task-configs.json) ---

export interface TaskFieldOverride {
  taskId: string;
  title?: string;
  description?: string;
  groupLabel?: string;
  noteRequired?: boolean;
  /** null clears an inherited dropdown (revert to a checkbox task) */
  dropdownId?: string | null;
  /** null clears an inherited condition */
  showWhen?: TaskFieldCondition | null;
}

export interface TaskSectionOverride {
  sectionId: string;
  label?: string;
  showWhen?: TaskFieldCondition | null;
  /** Explicit reorder position (0-based) */
  order?: number;
  taskOverrides?: TaskFieldOverride[];
  addTasks?: TaskDefinition[];
  /** taskIds to exclude from this section */
  removeTasks?: string[];
}

export interface TaskListReference {
  taskListId: string;
  showWhen?: TaskFieldCondition;
  labelOverride?: string;
  sectionOverrides?: TaskSectionOverride[];
  addSections?: TaskSectionDefinition[];
  /** sectionIds to exclude */
  removeSections?: string[];
}

/**
 * Template task configuration — an ordered list of sections that may be
 * inline definitions or references to reusable task lists.
 *
 * Discriminate by checking for `taskListId` property on each entry.
 */
export interface TemplateTaskConfig {
  ticketTypeId: string;
  sections: Array<TaskSectionDefinition | TaskListReference>;
}

export interface TemplateTaskConfigCatalog {
  schemaVersion: number;
  configs: TemplateTaskConfig[];
}

// --- Type guard helpers ---

export function isTaskListReference(
  section: TaskSectionDefinition | TaskListReference
): section is TaskListReference {
  return 'taskListId' in section;
}

// --- Saved checklist (DV field cr001_task_list) ---

export interface SavedTask {
  taskId: string;
  title: string;
  description?: string;
  /** Optional reference URL, captured for fidelity (see TaskDefinition.url). */
  url?: string;
  groupLabel?: string;
  status: TaskStatus;
  /** Technician free-text note */
  note?: string;
  /** Whether a note was required to complete this task (captured for fidelity). */
  noteRequired?: boolean;
  /** For a dropdown task: the reusable option set it used. */
  dropdownId?: string;
  /** For a dropdown task: the option the technician selected. */
  selectedOption?: string;
  isRemoved: boolean;
  /** true when showWhen condition evaluated false */
  isHidden: boolean;
  source: 'definition' | 'manual';
}

export interface SavedSection {
  sectionId: string;
  label: string;
  sourceTaskListId?: string;
  isRemoved: boolean;
  isHidden: boolean;
  tasks: SavedTask[];
}

export interface SavedChecklist {
  schemaVersion: number;
  /** ISO timestamp of when the checklist was frozen */
  snapshotAt: string;
  sections: SavedSection[];
  /** Preserved conditions for potential re-evaluation */
  conditions?: {
    tasks: Record<string, TaskFieldCondition>;
    sections: Record<string, TaskFieldCondition>;
  };
}

// --- Saved audit (DV field cr001_task_audit) ---

export interface SavedAudit {
  schemaVersion: number;
  /** Human-readable strings, each including user identity and timestamp */
  entries: string[];
}
